import { FaHouse, FaLocationCrosshairs, FaUser, FaBuilding, FaAnglesLeft, FaBriefcase, FaClock } from "react-icons/fa6"
import { AiOutlineAppstore } from "react-icons/ai"
import { Navigate } from "react-router-dom"
import { useContext, useEffect, useState } from "react"
import UiContext from "../../context/uiProvider"
import { LARGE_DESKTOP, DESKTOP, LARGE_TABLET, TABLET, MOBILE } from "../../utils/screenSize"
import useUser from "../../hooks/useUser"
import SidebarLink from "./SidebarLink"
import SmallLogo from "../../assets/logo-white-no-background.png"
import Tooltip from "../tooltips/Tooltip"
import { MANAGER, OWNER } from "../../utils/roles"
import { usePublicSiteBranding, usePublicSiteId, useSiteTerminology } from "../../contexts/PublicSiteContext"
import { isConstructionWalk } from "../../utils/publicSite"
import { getConstructionWalkNav } from "../../utils/companyNav"
import { useSelectedCompany } from "../../context/companyProvider"
import { isCompanyAdmin } from "../../utils/companyAccess"

const constructionIcons: Record<string, JSX.Element> = {
  "/app": <FaHouse className="text-3xl" />,
  "/app/management-companies": <FaBuilding className="text-3xl" />,
  "/app/locations": <FaLocationCrosshairs className="text-3xl" />,
  "/app/employees": <FaUser className="text-3xl" />,
  "/app/admin-mode": <FaBriefcase className="text-3xl" />,
  "/app/time-clock": <FaClock className="text-3xl" />,
}

export default function Sidebar() {
  const { sidebarOpen: open, setSidebarOpen: setOpen, deviceView, setDeviceView } = useContext(UiContext)
  const { roleId } = useUser()
  const siteId = usePublicSiteId()
  const branding = usePublicSiteBranding()
  const { managementCompanyPlural, areasLabel, locationsLabel } = useSiteTerminology()
  const { hasSelectedCompany } = useSelectedCompany()
  const [sidebarWidth, setSidebarWidth] = useState("w-20")

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1440) {
        setDeviceView(LARGE_DESKTOP)
        setSidebarWidth("w-64")
      } else if (window.innerWidth >= 1280) {
        setDeviceView(DESKTOP)
        setSidebarWidth("w-64")
      } else if (window.innerWidth > 1024) {
        setDeviceView(LARGE_TABLET)
        setSidebarWidth("w-64")
      } else if (window.innerWidth >= 768) {
        setDeviceView(TABLET)
        setSidebarWidth("w-56")
      } else {
        setDeviceView(MOBILE)
        setSidebarWidth("w-14")
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [setDeviceView])

  const transitionLink = `transition-opacity ease-in ${open ? "duration-700 opacity-100 pl-3 text-lg" : "duration-100 opacity-0 w-0 p-0 absolute overflow-hidden"}`

  if (!roleId) return <Navigate to={"/login"} replace />

  const construction = isConstructionWalk(siteId)
  const constructionItems = construction
    ? getConstructionWalkNav({
      isAdmin: isCompanyAdmin(roleId),
      hasSelectedCompany,
    })
    : []

  return (
    <div className={`h-screen z-20 max-h-screen`}>
      <div className={`bg-primary-dark relative ease-in duration-300 text-white h-full shadow-right-lg ${deviceView < MOBILE ? "w-14 pt-" : `${open ? sidebarWidth : "w-20"} text-xl`}`}>
        <div className={`w-full flex justify-center px-6 py-6 ${!open && "invisible"}`}>
          <img
            src={branding.markLogoSrc ?? SmallLogo}
            alt={branding.navbarLogoAlt}
            className={branding.sidebarLogoClass ?? 'max-w-full'}
          />
        </div>
        {deviceView >= TABLET && <FaAnglesLeft className={`text-white z-30 text-3xl -right-3 absolute top-9 bg-primary-dark ease-in duration-300 rounded-full p-1 ${!open && "rotate-180"}`} onClick={() => setOpen(!open)} />}

        {construction ? (
          constructionItems.map((item) => (
            <SidebarLink key={item.to} to={item.to} data-testid={item.testId}>
              <Tooltip text={item.label}>
                {constructionIcons[item.to] || <FaHouse className="text-3xl" />}
              </Tooltip>
              {deviceView >= TABLET && (
                <span className={transitionLink} data-testid={item.testId}>{item.label}</span>
              )}
            </SidebarLink>
          ))
        ) : (
          <>
            <SidebarLink to={"/app/" as "dashboard"}>
              <Tooltip text="Open Dashboard">
                <FaHouse className="text-3xl" />
              </Tooltip>
              {deviceView >= TABLET && (
                <Tooltip text="Drag tables to change order">
                  <span className={transitionLink}>Dashboard</span>
                </Tooltip>
              )}
            </SidebarLink>
            {roleId > OWNER && (
              <SidebarLink to={"/app/management-companies"}>
                <Tooltip text={`View ${managementCompanyPlural}`}>
                  <FaBuilding className="text-3xl" />
                </Tooltip>
                {deviceView >= TABLET && (
                  <Tooltip text={`View and manage companies`}>
                    <div className={transitionLink}>
                      {managementCompanyPlural.split(' ').map((word, i) => (
                        <div key={i}>{word}</div>
                      ))}
                    </div>
                  </Tooltip>
                )}
              </SidebarLink>
            )}
            {roleId > MANAGER && (
              <SidebarLink to={"/app/organizations"}>
                <AiOutlineAppstore className="text-3xl" />
                {deviceView >= TABLET && <span className={transitionLink}>{areasLabel}</span>}
              </SidebarLink>
            )}
            <SidebarLink to={"/app/locations"}>
              <FaLocationCrosshairs className="text-3xl" />
              {deviceView >= TABLET && <span className={transitionLink}>{locationsLabel}</span>}
            </SidebarLink>
            {roleId > MANAGER && (
              <SidebarLink to={"/app/users"}>
                <FaUser className="text-3xl" />
                {deviceView >= TABLET && <span className={transitionLink}>Users</span>}
              </SidebarLink>
            )}
          </>
        )}
      </div>
    </div>
  )
}
